import { useCurrentUser } from "@3gaam/shared/pcapp/components/useUserStore";
import { useEffect } from "react";
import { breakpointSizes } from "./useBreakpoints";
import { localStorageGet } from "@3gaam/shared/utils/localStorage";
import { EssentialsOutput, ApplicantEssentials } from "@3gaam/api/types/essentials";

export type GoftinoUserPortable = {
  FullName?: String | null;
  Mobile?: String | null;
};

type GoftinoHookProviderProps = {
  goftinoUser: GoftinoUserPortable | null;
  initialMarginBottom: number | null
};

export const useGoftino = ({ goftinoUser, initialMarginBottom }: GoftinoHookProviderProps) => {
  let { currentUser } = useCurrentUser();
  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkIsSmallSizeScreen = () => {
      // Use matchMedia to detect mobile based on width 
      // we use this function for preevent re-render by show virtual keyboard in mobile device
      return window.matchMedia(`(max-width: ${breakpointSizes.lg}px)`).matches;
    };

    const getFullNameFromEssential = (essential?: ApplicantEssentials): string | null => {
      if (!essential)
        return null;

      if (essential.FirstName && essential.LastName)
        return `${essential.FirstName} ${essential.LastName}`;

      if (essential.FirstName)
        return essential.FirstName;

      if (essential.LastName)
        return essential.LastName;

      return null;
    }

    const getGoftinoUser = (goftinoUser: GoftinoUserPortable | null): GoftinoUserPortable => {
      if (goftinoUser)
        return goftinoUser;

      const _essentialsObj = localStorageGet("ESSENTIALS") as EssentialsOutput;
      return {
        FullName: getFullNameFromEssential(_essentialsObj?.Applicant) || currentUser?.Fullname,
        Mobile: _essentialsObj?.Applicant?.Mobile
      }
    }

    const goftinoReadyHandler = () => {
      const isSmallSizeScreen = checkIsSmallSizeScreen();
      if (isSmallSizeScreen) {
        //@ts-ignore
        window.Goftino.setWidget({ marginBottom: initialMarginBottom || 150 });
      }

      const currentGoftinoUser = getGoftinoUser(goftinoUser);
      if (!currentGoftinoUser.FullName && !currentGoftinoUser.Mobile)
        return;

      //goftino only accept null or value not undefined
      //@ts-ignore
      window.Goftino.setUser({
        email: null,
        phone: currentGoftinoUser.Mobile || null,
        name: currentGoftinoUser.FullName || null,
        forceUpdate: true,
      });
    };

    const injectGoftinoScriptTag = () => {
      const goftinoId = "EfVVKB"; // Replace with your actual Goftino widget ID
      const localStorageKey = `goftino_${goftinoId}`;
      const localStorageValue = localStorage.getItem(localStorageKey);
      const src = localStorageValue ? `https://www.goftino.com/widget/${goftinoId}?o=${localStorageValue}` : `https://www.goftino.com/widget/${goftinoId}`;

      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      script.id = 'goftino-chat-script';

      document.head.appendChild(script);
      window.addEventListener("goftino_ready", goftinoReadyHandler);
    };

    const timeoutId = setTimeout(() => {
      injectGoftinoScriptTag();
    }, 15000);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("goftino_ready", goftinoReadyHandler);
      document.getElementById("goftino-chat-script")?.remove();
      document.getElementById("goftino_w")?.remove();
    };
  }, [currentUser?.Fullname, initialMarginBottom, goftinoUser]);
};
